/**
 * Create / update / delete / test arbitium_sources documents.
 */

import { ARBITIUM_EXTENSION, callHandler, deleteRingDoc, postRing, putRingDoc } from '../core';
import type { SourceItem } from './get_sources';

const RING_SOURCES = 'arbitium_sources';
export const STARTER_KEY = 'platform_cloudtrail';

export type SourceFormFields = {
  name: string;
  provider: string;
  status: string;
  dropzone_type: string;
  bucket: string;
  prefix: string;
  prefix_filter: string;
  region: string;
  assume_role_arn: string;
  object_filter: string;
  normalize_mode: string;
  adapter_uri: string;
  candidate_source: string;
  starter_key?: string;
};

export const EMPTY_SOURCE_FORM: SourceFormFields = {
  name: '',
  provider: 'aws',
  status: 'inactive',
  dropzone_type: 's3',
  bucket: '',
  prefix: 'candidates/aws/',
  prefix_filter: '',
  region: 'us-east-1',
  assume_role_arn: '',
  object_filter: '*.json',
  normalize_mode: 'builtin:cloudtrail',
  adapter_uri: '',
  candidate_source: 'aws',
};

export const NORMALIZE_MODE_OPTIONS = [
  { value: 'pre_normalized', label: 'pre_normalized (Envelope v1)' },
  { value: 'builtin:cloudtrail', label: 'builtin:cloudtrail' },
  { value: 'builtin:splunk-qa-paloalto', label: 'builtin:splunk-qa-paloalto' },
  { value: 'builtin:splunk-qa-cloudtrail', label: 'builtin:splunk-qa-cloudtrail' },
  { value: 'builtin:splunk-qa-okta', label: 'builtin:splunk-qa-okta' },
  { value: 'module', label: 'module (reserved)' },
] as const;

export type SourceFieldKey =
  | 'overview'
  | 'name'
  | 'provider'
  | 'status'
  | 'normalize_mode'
  | 'candidate_source'
  | 'bucket'
  | 'prefix'
  | 'prefix_filter'
  | 'region'
  | 'object_filter'
  | 'assume_role_arn'
  | 'adapter_uri';

export const SOURCE_FIELD_HELP: Record<SourceFieldKey, { title: string; body: string }> = {
  overview: {
    title: 'How Sources work',
    body: 'Sources describe S3 drop zones for ingest. Prefix fences a root (e.g. splunk_dates/) and scan recurses into date subfolders — it never lists parents. With zero active Sources, Batch uses the legacy threat-events bucket under candidates/<source>/*.json. If any Source is active, only those drop zones are scanned.',
  },
  name: {
    title: 'Name',
    body: 'Human label for this connection (e.g. CloudTrail platform bucket, Palo Alto Splunk).',
  },
  provider: {
    title: 'Provider',
    body: 'Vendor family (aws, paloalto, okta, …). Used for analytics and as the default candidate_source when that field is empty.',
  },
  status: {
    title: 'Status',
    body: 'inactive: ignored by scan. active: included in scan_candidates. Activating any Source turns off the legacy candidates/ path until all are inactive again.',
  },
  normalize_mode: {
    title: 'Normalize mode',
    body: 'builtin:cloudtrail — vendor CloudTrail JSON. builtin:splunk-qa-* — Splunk CSV exports. pre_normalized — Envelope v1 JSON. module — reserved.',
  },
  candidate_source: {
    title: 'Candidate source',
    body: 'Slug stored on each candidate (e.g. aws, paloalto). Lab routes by this value. Defaults to provider when empty.',
  },
  bucket: {
    title: 'Bucket',
    body: 'S3 bucket name only (no s3://).',
  },
  prefix: {
    title: 'Prefix',
    body: 'Root fence under the bucket. Example: splunk_dates/ or candidates/aws/.',
  },
  prefix_filter: {
    title: 'Prefix filter',
    body: 'Optional substring or glob on the object filename/key (e.g. export_paloalto). Empty = no name filter.',
  },
  region: {
    title: 'Region',
    body: 'AWS region for List/Get on this bucket.',
  },
  object_filter: {
    title: 'Object filter',
    body: 'Filename glob (default *.json for CloudTrail JSON; use *.csv for Splunk QA).',
  },
  assume_role_arn: {
    title: 'Assume role ARN',
    body: 'Reserved for cross-account read. Not assumed automatically yet — grant the Arbitium runtime role on the bucket instead.',
  },
  adapter_uri: {
    title: 'Adapter URI',
    body: 'Only for normalize_mode=module (future). Leave empty for CloudTrail, Splunk QA, and Envelope.',
  },
};

export function sourceToForm(item?: Partial<SourceItem> | null): SourceFormFields {
  if (!item) return { ...EMPTY_SOURCE_FORM };
  return {
    name: String(item.name || ''),
    provider: String(item.provider || 'aws'),
    status: String(item.status || 'inactive'),
    dropzone_type: String(item.dropzone_type || 's3'),
    bucket: String(item.bucket || ''),
    prefix: String(item.prefix || ''),
    prefix_filter: String(item.prefix_filter || ''),
    region: String(item.region || 'us-east-1'),
    assume_role_arn: String(item.assume_role_arn || ''),
    object_filter: String(item.object_filter || '*.json'),
    normalize_mode: String(item.normalize_mode || 'builtin:cloudtrail'),
    adapter_uri: String(item.adapter_uri || ''),
    candidate_source: String(item.candidate_source || ''),
    starter_key: item.starter_key ? String(item.starter_key) : undefined,
  };
}

function formToPayload(form: SourceFormFields): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: form.name.trim(),
    provider: (form.provider || 'other').trim(),
    status: form.status || 'inactive',
    dropzone_type: 's3',
    bucket: form.bucket.trim(),
    prefix: (form.prefix || '').trim(),
    prefix_filter: (form.prefix_filter || '').trim(),
    region: (form.region || 'us-east-1').trim(),
    assume_role_arn: (form.assume_role_arn || '').trim(),
    object_filter: (form.object_filter || '*.json').trim(),
    normalize_mode: form.normalize_mode || 'builtin:cloudtrail',
    adapter_uri: (form.adapter_uri || '').trim(),
    candidate_source: (form.candidate_source || '').trim(),
  };
  if (form.starter_key) body.starter_key = form.starter_key;
  return body;
}

export async function createSource(form: SourceFormFields): Promise<SourceItem> {
  if (!form.name.trim() || !form.bucket.trim()) {
    throw new Error('name and bucket are required');
  }
  const res = await postRing<SourceItem>(RING_SOURCES, formToPayload(form));
  if (!res.success || !res.item) {
    throw new Error(res.error || res.message || 'Failed to create source');
  }
  return res.item;
}

export async function updateSource(id: string, form: SourceFormFields): Promise<void> {
  if (!form.name.trim() || !form.bucket.trim()) {
    throw new Error('name and bucket are required');
  }
  const res = await putRingDoc(RING_SOURCES, id, formToPayload(form));
  if (res.success === false) {
    throw new Error(res.error || res.message || 'Failed to update source');
  }
}

export async function updateSourceStatus(id: string, status: 'active' | 'inactive'): Promise<void> {
  const res = await putRingDoc(RING_SOURCES, id, { status });
  if (res.success === false) {
    throw new Error(res.error || res.message || 'Failed to update status');
  }
}

export async function deleteSource(id: string): Promise<void> {
  const res = await deleteRingDoc(RING_SOURCES, id);
  if (res.success === false) {
    throw new Error(res.error || (res as { message?: string }).message || 'Failed to delete source');
  }
}

export async function testSourceIngest(sourceId: string): Promise<{
  ok: boolean;
  lastStatus: string;
  raw: unknown;
}> {
  const data = await callHandler(ARBITIUM_EXTENSION, 'scan_candidates', {
    test_only: true,
    source_id: sourceId,
    max_objects: 1,
    trigger_lab_batch: false,
  });
  const nested = (data?.stack?.output || data?.output || data) as Record<string, unknown>;
  const tested = (nested?.output as Record<string, unknown> | undefined)?.tested || nested?.tested || [];
  const testedArr = Array.isArray(tested) ? tested : [];
  const first = testedArr[0] as { ok?: boolean; note?: string; error?: string } | undefined;
  const lastStatus =
    String(
      (nested?.output as Record<string, unknown> | undefined)?.last_status ||
        nested?.last_status ||
        (first
          ? first.ok
            ? `OK: ${first.note || 'Test ingest OK'}`
            : `FAILED: ${first.error || 'Test ingest failed'}`
          : '')
    ) || '';
  const ok =
    data?.success !== false &&
    nested?.success !== false &&
    (testedArr.length === 0 || testedArr.every((t) => (t as { ok?: boolean }).ok !== false));
  return { ok, lastStatus, raw: data };
}

export async function ensureCloudTrailStarter(): Promise<{ created: boolean; message: string }> {
  const data = await callHandler(ARBITIUM_EXTENSION, 'scan_candidates', {
    ensure_starter: true,
    trigger_lab_batch: false,
  });
  const nested = (data?.stack?.output || data?.output || data) as Record<string, unknown>;
  const ok = data?.success !== false && nested?.success !== false;
  if (!ok) {
    throw new Error(
      String(data?.error || nested?.error || nested?.message || data?.message || 'Failed to ensure starter')
    );
  }
  const created = Boolean(
    (nested?.output as Record<string, unknown> | undefined)?.created ?? nested?.created
  );
  return {
    created,
    message: created
      ? 'Starter CloudTrail source created (inactive)'
      : 'Starter CloudTrail source already present',
  };
}
